// src/modules/module_step1.tsx
import React, { useEffect, useState } from 'react';
import config from '../config/config';
import { CardSubtitle, Card, CardHeader, CardTitle, CardBody } from '@progress/kendo-react-layout';
import styled from 'styled-components';
import { Button } from '@progress/kendo-react-buttons';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { DateTimePicker, DateTimePickerChangeEvent } from '@progress/kendo-react-dateinputs';
import { Input, TextArea } from '@progress/kendo-react-inputs';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import axios from 'axios';
import { useForm, FormState } from '../contexts/FormContext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import GetUserAvatar from '../components/GetUserAvatar';
import { SvgIcon } from '@progress/kendo-react-common';
import { fileDataIcon } from '@progress/kendo-svg-icons';

// Lokale Erweiterung von FormState
interface ExtendedFormState extends FormState {
  latitude?: number | null;
  longitude?: number | null;
  isAllDay?: boolean;
  ansprechpartner?: { name: string; email: string; phone: string; jobrole?: string }[];
  jobDocuments?: string[];
  internalNotes?: string;
  kurzbeschreibung?: string; // optional gespeichert, falls später benötigt
}

const filenameFromUrl = (url: string): string =>
  decodeURIComponent(url.split('/').pop() || '');

const StyledButton = styled(Button)`
  display: block;
  margin-left: auto;
  font-weight: bold;
  font-size: 20px;
`;

const ContactPersonContainer = styled.div`
  margin-bottom: 20px;
`;

const ContactPersonFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const NotesContainer = styled.div`
  margin-bottom: 20px;
`;

// Neue Styled-Komponente für die Labels mit abgerundeten Ecken und Kendo-Primary-Farbe
const StyledLabel = styled.label`
  display: inline-block;
  width: 200px;
  padding: 4px 10px;
  border: 1px solid #99a4ae; /* Outline in #99a4ae */
  color: rgb(82, 82, 82); /* Textfarbe passt zur Outline */
  border-radius: 12px; /* Abgerundete Ecken */
  font-size: 14px;
  margin-bottom: 4px;
  margin-left: 2px;
`;

// Styled-Komponente für die Textvorschau
const TextPreview = styled.pre`
  white-space: pre-wrap;
  word-wrap: break-word;
  max-height: calc(100% - 20px);
  overflow-y: auto;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: #f9f9f9;
`;

// Styled-Komponente für die Bildvorschau
const ImagePreview = styled.img`
  max-width: 100%;
  max-height: calc(100% - 20px);
  object-fit: contain;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: #f9f9f9;
`;

const ModuleStep1: React.FC = () => {
  const { formData, setFormData } = useForm() as {
    formData: ExtendedFormState;
    setFormData: (data: ExtendedFormState | ((prev: ExtendedFormState) => ExtendedFormState)) => void;
  };
  const [jobIDOptions, setJobIDOptions] = useState<{ text: string; value: string }[]>([]);
  const { userId } = useAuth();
  const navigate = useNavigate();

  // State für das Modal / den Dokument-Viewer
  const [showViewer, setShowViewer] = useState(false);
  const [viewerSrc, setViewerSrc] = useState<string>('');
  const [textContent, setTextContent] = useState<string>('');
  const [isTextFile, setIsTextFile] = useState(false);
  const [isImageFile, setIsImageFile] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');

  /* ------------------------------------------------------------ */
  /* Datei-Vorschau: Textdateien und Bilder clientseitig, PDF direkt, andere mit Office Viewer */
  /* ------------------------------------------------------------ */
  const openViewer = async (url: string) => {
    setIsLoading(true);
    setError('');
    setTextContent('');
    setIsTextFile(false);
    setIsImageFile(false);
    setViewerSrc('');

    try {
      // Dateiendung prüfen
      const fileExtension = url.split('.').pop()?.toLowerCase();

      if (fileExtension === 'txt') {
        // Textdatei: Inhalt clientseitig abrufen
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error('Fehler beim Abrufen der Textdatei');
        }
        const text = await response.text();
        setTextContent(text);
        setIsTextFile(true);
      } else if (['png', 'jpg', 'jpeg', 'webp'].includes(fileExtension || '')) {
        // Bilddatei: URL für die Vorschau verwenden
        setViewerSrc(url);
        setIsImageFile(true);
      } else if (fileExtension === 'pdf') {
        // PDF direkt im Browser iframe darstellen
        setViewerSrc(url);
        setIsTextFile(false);
        setIsImageFile(false);
      } else {
        // Andere Formate: Office Viewer
        const embeddedUrl = `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(url)}`;
        setViewerSrc(embeddedUrl);
        setIsTextFile(false);
        setIsImageFile(false);
      }

      setShowViewer(true);
    } catch (err) {
      console.error('❌ Fehler beim Öffnen der Datei:', err);
      setError('Fehler beim Laden der Datei. Bitte überprüfe die URL oder versuche es später erneut.');
    } finally {
      setIsLoading(false);
    }
  };

  /* ------------------------------------------------------------ */
  /* Navigation zu Google Maps                                   */
  /* ------------------------------------------------------------ */
  const handleNavigation = () => {
    const { EinsatzPLZ, EinsatzORT, EinsatzStrasse, EinsatzHausnummer, latitude, longitude } = formData;

    if (
      latitude !== null &&
      latitude !== undefined &&
      longitude !== null &&
      longitude !== undefined
    ) {
      const googleMapsUrl = `https://maps.google.com/maps/dir//${latitude},${longitude}/data=!4m7!4m6!1m1!4e2!1m2!1m1!1s0x0:0x0!3e0!11m1!6b1?entry=s&sa=X&ved=1t:6881&ictx=111`;
      window.location.href = googleMapsUrl;
    } else if (EinsatzPLZ && EinsatzORT && EinsatzStrasse && EinsatzHausnummer) {
      const address = `${EinsatzPLZ} ${EinsatzORT} ${EinsatzStrasse} ${EinsatzHausnummer}`;
      const encodedAddress = encodeURIComponent(address).replace(/%20/g, '+');
      const googleMapsUrl = `https://maps.google.com/maps/dir//${encodedAddress}/data=!4m7!4m6!1m1!4e2!1m2!1m1!1s0x0:0x0!3e0!11m1!6b1?entry=s&sa=X&ved=1t:6881&ictx=111`;
      window.location.href = googleMapsUrl;
    } else {
      console.error(
        'Fehlende Daten für die Navigation: Weder Koordinaten noch vollständige Adresse vorhanden'
      );
    }
  };

  useEffect(() => {
    if (!userId) {
      console.error('❌ Kein eingeloggter Benutzer gefunden.');
      navigate('/login', { replace: true });
      return;
    }

    const fetchUserJobs = async () => {
      try {
        const jobsResponse = await axios.get(`${config.host}/jobs`);
        const allJobs = jobsResponse.data;

        // Nur aktive, dem Benutzer zugewiesene Jobs übernehmen
        const assignedJobs = allJobs.filter((job: any) => {
          const isActive =
            job.JobActive === true ||
            (typeof job.JobActive === 'string' && job.JobActive.toLowerCase() === 'true');
          return (
            Array.isArray(job.AssignedEmployee) &&
            job.AssignedEmployee.includes(userId) &&
            isActive
          );
        });

        // Nach Datum absteigend sortieren (neuester Auftrag zuerst)
        assignedJobs.sort((a: any, b: any) => {
          const dateA = new Date(a.AusfuehrungszeitStart || 0).getTime();
          const dateB = new Date(b.AusfuehrungszeitStart || 0).getTime();
          return dateB - dateA;
        });

        // Dropdown-Optionen erzeugen (Wochentag, TT.MM.YYYY, Uhrzeit, Kurzbeschreibung)
        const jobList = assignedJobs.map((job: any) => ({
          text: [
            formatDateWithTime(job.AusfuehrungszeitStart || '', job.isAllDay || false),
            job.Kurzbeschreibung || '',
          ]
            .filter(Boolean)
            .join(', '),
          value: job.JobID,
        }));

        setJobIDOptions(jobList);
      } catch (error) {
        console.error('❌ Fehler beim Laden der zugewiesenen Jobs:', error);
      }
    };

    fetchUserJobs();
  }, [userId, navigate]);

  const formatDateWithTime = (dateString: string, isAllDay: boolean): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const baseOptions: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    };
    if (isAllDay) {
      return new Intl.DateTimeFormat('de-DE', baseOptions).format(date);
    }
    const options: Intl.DateTimeFormatOptions = {
      ...baseOptions,
      hour: '2-digit',
      minute: '2-digit',
    };
    return new Intl.DateTimeFormat('de-DE', options).format(date);
  };

  const handleJobChange = (event: DropDownListChangeEvent) => {
    const selectedJobID = event.value?.value || event.value;
    console.log('📌 Gewählter Job ID:', selectedJobID);

    setFormData((prev: ExtendedFormState) => ({
      ...prev,
      jobID: selectedJobID,
    }));

    axios
      .get(`${config.host}/jobs/${selectedJobID}`)
      .then((response) => {
        const jobData = response.data;
        setFormData((prev: ExtendedFormState) => ({
          ...prev,
          AusfuehrungszeitStart: jobData.AusfuehrungszeitStart || '',
          AusfuehrungszeitEnde: jobData.AusfuehrungszeitEnde || '',
          customerID: jobData.CustomerID || '',
          EinsatzPLZ: jobData.EinsatzPLZ || '',
          EinsatzORT: jobData.EinsatzORT || '',
          EinsatzStrasse: jobData.EinsatzStrasse || '',
          EinsatzHausnummer: jobData.EinsatzHausnummer || '',
          latitude: jobData.latitude || null,
          longitude: jobData.longitude || null,
          isAllDay: jobData.isAllDay || false,
          ansprechpartner: Array.isArray(jobData.Ansprechpartner)
            ? jobData.Ansprechpartner.map((person: any) => ({
                name: person.name || '',
                email: person.email || '',
                phone: person.phone || '',
                jobrole: person.jobrole || '',
              }))
            : [],
          internalNotes: jobData.InternalNotes || '',
          kurzbeschreibung: jobData.Kurzbeschreibung || '',
          jobDocuments: Array.isArray(jobData.JobDocuments)
            ? jobData.JobDocuments.filter(Boolean)
            : [],
        }));

        // Firma aus EBTB-CUSTOMERS laden
        if (jobData.CustomerID) {
          axios
            .get(`${config.host}/customers/${jobData.CustomerID}`)
            .then((response) => {
              const customerData = response.data;
              setFormData((prev: ExtendedFormState) => ({
                ...prev,
                firma: customerData.Firma || '',
              }));
            })
            .catch((error) => console.error('❌ Fehler beim Laden der Kundendaten:', error));
        }
      })
      .catch((error) => console.error('❌ Fehler beim Laden des Jobs:', error));
  };

  const handleFieldChange = (field: string, value: any) => {
    setFormData((prev: ExtendedFormState) => ({
      ...prev,
      [field]: value,
    }));
    axios
      .put(`${config.host}/jobs/${formData.jobID}`, { [field]: value })
      .then(() => console.log(`📌 ${field} erfolgreich aktualisiert`))
      .catch((error) => console.error(`❌ Fehler beim Speichern von ${field}:`, error));
  };

  return (
    <div style={{ padding: '20px' }}>
      <Card orientation="vertical">
        <CardHeader className="k-hbox custom-card-header">
          <GetUserAvatar />
          <div>
            <CardTitle>Step 1: Projektdaten</CardTitle>
            <CardSubtitle>Bitte wähle ein dir zugewiesenes Projekt aus</CardSubtitle>
          </div>
        </CardHeader>
        <CardBody>
          <div style={{ marginBottom: '20px' }}>
            <StyledLabel>Job ID</StyledLabel>
            <DropDownList
              data={jobIDOptions}
              value={jobIDOptions.find((job) => job.value === formData.jobID) || null}
              className="custom-dropdown"
              textField="text"
              dataItemKey="value"
              onChange={handleJobChange}
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <StyledButton
              themeColor="primary"
              style={{ width: '100%', marginTop: '20px', marginBottom: '20px' }}
              onClick={handleNavigation}
            >
              Öffne Navigation
            </StyledButton>
          </div>
          <div style={{ marginBottom: '20px' }}>
            <StyledLabel>Startdatum und Uhrzeit</StyledLabel>
            <DateTimePicker
              format={formData.isAllDay ? 'dd.MM.yyyy' : 'dd.MM.yyyy HH:mm'}
              value={formData.AusfuehrungszeitStart ? new Date(formData.AusfuehrungszeitStart) : null}
              onChange={(e: DateTimePickerChangeEvent) => {
                const selectedDate = e.value;
                if (selectedDate) {
                  const isoString = selectedDate.toISOString();
                  handleFieldChange('AusfuehrungszeitStart', isoString);
                }
              }}
              width="100%"
            />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <StyledLabel>Enddatum und Uhrzeit</StyledLabel>
            <DateTimePicker
              format={formData.isAllDay ? 'dd.MM.yyyy' : 'dd.MM.yyyy HH:mm'}
              value={formData.AusfuehrungszeitEnde ? new Date(formData.AusfuehrungszeitEnde) : null}
              onChange={(e: DateTimePickerChangeEvent) => {
                const selectedDate = e.value;
                if (selectedDate) {
                  const isoString = selectedDate.toISOString();
                  handleFieldChange('AusfuehrungszeitEnde', isoString);
                }
              }}
              width="100%"
            />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <StyledLabel>Firma</StyledLabel>
            <Input value={formData.firma} disabled style={{ width: '100%' }} />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <StyledLabel>Straße</StyledLabel>
            <Input value={formData.EinsatzStrasse} disabled style={{ width: '100%' }} />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <StyledLabel>Hausnummer</StyledLabel>
            <Input value={formData.EinsatzHausnummer} disabled style={{ width: '100%' }} />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <StyledLabel>PLZ</StyledLabel>
            <Input value={formData.EinsatzPLZ} disabled style={{ width: '100%' }} />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <StyledLabel>Ort</StyledLabel>
            <Input value={formData.EinsatzORT} disabled style={{ width: '100%' }} />
          </div>
          <h3>Ansprechpartner</h3>
          {formData.ansprechpartner && formData.ansprechpartner.length > 0 ? (
            formData.ansprechpartner.map((person, index) => (
              <ContactPersonContainer key={index}>
                <ContactPersonFields>
                  {person.name && (
                    <div style={{ marginBottom: '20px' }}>
                      <StyledLabel>{`AP ${index + 1}`}</StyledLabel>
                      <Input value={person.name} disabled style={{ width: '100%' }} />
                    </div>
                  )}
                  {person.jobrole && (
                    <div style={{ marginBottom: '20px' }}>
                      <StyledLabel>{`Rolle AP ${index + 1}`}</StyledLabel>
                      <Input value={person.jobrole} disabled style={{ width: '100%' }} />
                    </div>
                  )}
                  {person.email && (
                    <div style={{ marginBottom: '20px' }}>
                      <StyledLabel>{`E-Mail AP ${index + 1}`}</StyledLabel>
                      <Input value={person.email} disabled style={{ width: '100%' }} />
                    </div>
                  )}
                  {person.phone && (
                    <div style={{ marginBottom: '20px' }}>
                      <StyledLabel>{`Telefon AP ${index + 1}`}</StyledLabel>
                      <Input value={person.phone} disabled style={{ width: '100%' }} />
                    </div>
                  )}
                </ContactPersonFields>
              </ContactPersonContainer>
            ))
          ) : (
            <p>Keine Ansprechpartner verfügbar.</p>
          )}
          <h3>Notizen</h3>
          <NotesContainer>
            <TextArea
              value={formData.internalNotes || ''}
              readOnly
              style={{ width: '100%', height: '150px' }}
            />
          </NotesContainer>

          {/* ---------------- Job-Dokumente -------------------------------- */}
          {formData.jobDocuments && formData.jobDocuments.length > 0 && (
            <>
              <h3>Job-Dokumente</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 15 }}>
                {formData.jobDocuments.map((url, idx) => (
                  <Card key={idx} style={{ width: 180, boxShadow: '0 0 4px rgba(0,0,0,.1)' }}>
                    <div style={{ padding: 10, wordBreak: 'break-all', textAlign: 'center' }}>
                      <SvgIcon icon={fileDataIcon} style={{ width: 24, height: 24 }} />
                      <br />
                      <a
                        href={url}
                        onClick={(e) => {
                          e.preventDefault();
                          openViewer(url);
                        }}
                        style={{ color: '#f5811e', fontSize: 12, textDecoration: 'none' }}
                      >
                        {filenameFromUrl(url)}
                      </a>
                    </div>
                  </Card>
                ))}
              </div>
            </>
          )}

          {/* ------------------------------------------------------------ */}
          {/* Modal für Dokument-Vorschau: Text, Bilder, PDF oder Office Viewer */}
          {/* ------------------------------------------------------------ */}
          {showViewer && (
            <Dialog
              title="Dokument-Vorschau"
              onClose={() => setShowViewer(false)}
              width="calc(100vw - 80px)"
              height="calc(100vh - 80px)"
            >
              {isLoading && <p>Lade Datei...</p>}
              {error && <p style={{ color: 'red' }}>{error}</p>}
              {!isLoading && !error && isTextFile && <TextPreview>{textContent}</TextPreview>}
              {!isLoading && !error && isImageFile && <ImagePreview src={viewerSrc} alt="Bildvorschau" />}
              {!isLoading && !error && !isTextFile && !isImageFile && (
                <iframe
                  title="Dokument-Vorschau"
                  src={viewerSrc}
                  width="100%"
                  height="100%"
                  style={{ border: 'none' }}
                />
              )}
              <DialogActionsBar>
                <Button onClick={() => setShowViewer(false)}>Schließen</Button>
              </DialogActionsBar>
            </Dialog>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default ModuleStep1;
